import { supabase } from './supabaseClient';
import { authStore } from './authStore';

/** A band the signed-in user belongs to, with their own role in it. Distinct
 * from the local-only BandRecord in types.ts (Song Library's band/project
 * filter) — this is the Supabase-backed, multi-user concept from
 * SUPABASE_SPEC.md, not yet connected to anything else in the app. */
export interface MyBand {
  id: string;
  name: string;
  role: 'host' | 'member';
  createdAt: string;
}

/** No local caching/reactivity here (unlike authStore) — callers re-fetch
 * on demand (e.g. after creating a band) rather than this module tracking
 * live state, since nothing yet needs to react to a band list changing
 * from outside the component that's showing it. */
export async function listMyBands(): Promise<{ bands: MyBand[]; error: string | null }> {
  if (!supabase) return { bands: [], error: 'Supabase is not configured in this build.' };
  const userId = authStore.getSession()?.user.id;
  if (!userId) return { bands: [], error: 'Not signed in.' };

  const { data, error } = await supabase.from('band_members').select('role, bands(id, name, created_at)').eq('user_id', userId);
  if (error) return { bands: [], error: error.message };

  const bands: MyBand[] = [];
  for (const row of data ?? []) {
    const band = row.bands as unknown as { id: string; name: string; created_at: string } | null;
    if (!band) continue;
    bands.push({ id: band.id, name: band.name, role: row.role as 'host' | 'member', createdAt: band.created_at });
  }
  return { bands, error: null };
}

/** Creating a band automatically makes the creator its first host — see
 * the handle_new_band trigger in the initial schema migration — so no
 * separate band_members insert is needed here. */
export async function createBand(name: string): Promise<{ error: string | null }> {
  if (!supabase) return { error: 'Supabase is not configured in this build.' };
  const userId = authStore.getSession()?.user.id;
  if (!userId) return { error: 'Not signed in.' };

  const { error } = await supabase.from('bands').insert({ name, owner_user_id: userId });
  return { error: error?.message ?? null };
}
